import Utils from "../../../../../../../../core/services/utils/utils";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

/**
 * Sets whether a part is public to other partners (`isPublicForBuyers`).
 */
export class ProductVisibilityToOtherPartnersImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     */
    async run(mappedData, isFinalRun = false) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            codes: [],
        }));

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        const uniqueParts = [...new Set(rowState.map((r) => r.partNoUpper).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        for (const rs of rowState) {
            if (!rs.item) {
                continue;
            }
            const isPublicForBuyers = Utils.tryConvertStringToBoolean(rs.item.isPublicForBuyers);
            if (isPublicForBuyers === undefined) {
                rs.codes.push(PreventionInvalidityCode.INVALID_BOOLEAN);
            }
        }

        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }
            if (!rs.codes.length) {
                continue;
            }
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun ? null : {partId: null, partNumber: undefined};
            }

            const rs = rowState[index];
            if (!rs.partNoUpper) {
                return isFinalRun ? null : {partId: null, partNumber: item.partNumber};
            }

            const partId = partIds[rs.partNoUpper];
            const hasIssue = issueIndexSet.has(index);

            if (hasIssue) {
                return isFinalRun
                    ? null
                    : {
                        partId: partId ?? null,
                        partNumber: item.partNumber,
                        isPublicForBuyers: Utils.tryConvertStringToBoolean(item.isPublicForBuyers) ?? null,
                    };
            }

            return {
                partId,
                partNumber: item.partNumber?.trim(),
                isPublicForBuyers: Utils.tryConvertStringToBoolean(item.isPublicForBuyers),
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
